import type { components } from "@majitap/sdk";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";
import type { AcquisitionSource } from "./labels";

export type Business = components["schemas"]["Business"];
export type MemberRole = Business["members"][number]["role"];

export const ROLES: Record<MemberRole, { label: string; help: string }> = {
  owner: { label: "Owner", help: "Orders for the business, sees all its orders, manages its members and API tokens" },
  manager: { label: "Manager", help: "Orders for the business and sees all its orders" },
  user: { label: "User", help: "Orders for the business and sees their own orders" },
};

export const businessQuery = (id: string) =>
  queryOptions({
    queryKey: ["business", id],
    queryFn: () => ok(api.GET("/v1/admin/businesses/{id}", { params: { path: { id } } })),
  });

export interface BusinessFields {
  business_name: string;
  tax_pin?: string;
  email?: string;
  phone?: string;
  parent_id?: string | null;
  acquisition_source?: AcquisitionSource;
  account_manager_staff_id?: string | null;
}

export const businessActions = {
  create: (body: BusinessFields) =>
    ok(
      api.POST("/v1/admin/businesses", {
        body: {
          ...body,
          parent_id: body.parent_id ?? undefined,
          account_manager_staff_id: body.account_manager_staff_id ?? undefined,
        },
      }),
    ),
  update: (
    id: string,
    body: Partial<Omit<BusinessFields, "tax_pin" | "email" | "phone">> & {
      // null removes it
      tax_pin?: string | null;
      email?: string | null;
      phone?: string | null;
      is_active?: boolean;
    },
  ) => ok(api.PATCH("/v1/admin/businesses/{id}", { params: { path: { id } }, body })),
  remove: (id: string) => ok(api.DELETE("/v1/admin/businesses/{id}", { params: { path: { id } } })),
  setMember: (id: string, customerId: string, role: MemberRole) =>
    ok(
      api.PUT("/v1/admin/businesses/{id}/members/{customer_id}", {
        params: { path: { id, customer_id: customerId } },
        body: { role },
      }),
    ),
  removeMember: (id: string, customerId: string) =>
    ok(
      api.DELETE("/v1/admin/businesses/{id}/members/{customer_id}", {
        params: { path: { id, customer_id: customerId } },
      }),
    ),
};
